// ── Theme mode — light mode disabled, always dark ──
let _mode = 'dark'

export const setThemeMode = () => {
  // Light mode disabled — this is a no-op
}

// One-time cleanup of old light mode setting
if (typeof localStorage !== 'undefined') localStorage.removeItem('syninn_theme')

export const getThemeMode = () => _mode

// ── Font stack — SYNINN typography system ──
// Display (hero, brand): Cormorant Garamond
// Body / UI: Inter (modern, neutral, screen-optimized)
// Codes / OTP / monospace: JetBrains Mono
const FONTS = {
  serif: "'Cormorant Garamond', 'Georgia', serif",
  sans:  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono:  "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
}

const DARK = {
  bg: '#080808', bg2: '#0f0f0f', bg3: '#141414', bg4: '#1a1a1a',
  gold: '#C9A84C', goldFaint: 'rgba(201,168,76,0.08)', goldBorder: 'rgba(201,168,76,0.25)',
  gold2: '#E8C96A',
  white: '#F5F0E8', muted: '#888', dimmed: '#555', faint: '#222', border: '#1e1e1e',
  red: '#e05c5c', redFaint: 'rgba(224,92,92,0.08)', redBorder: 'rgba(224,92,92,0.25)',
  green: '#4caf7d', greenFaint: 'rgba(76,175,125,0.08)', greenBorder: 'rgba(76,175,125,0.25)',
  blue: '#5c9ee0', blueFaint: 'rgba(92,158,224,0.08)', blueBorder: 'rgba(92,158,224,0.25)',
  serif: FONTS.serif,
  sans:  FONTS.sans,
  mono:  FONTS.mono,
}

const LIGHT = {
  bg: '#F8F6F2', bg2: '#FFFFFF', bg3: '#F0EDE8', bg4: '#E8E4DE',
  gold: '#B8922A', goldFaint: 'rgba(184,146,42,0.10)', goldBorder: 'rgba(184,146,42,0.30)',
  gold2: '#C9A84C',
  white: '#1A1A1A', muted: '#666', dimmed: '#999', faint: '#DDD', border: '#E0E0E0',
  red: '#C0392B', redFaint: 'rgba(192,57,43,0.08)', redBorder: 'rgba(192,57,43,0.25)',
  green: '#27AE60', greenFaint: 'rgba(39,174,96,0.08)', greenBorder: 'rgba(39,174,96,0.25)',
  blue: '#2980B9', blueFaint: 'rgba(41,128,185,0.08)', blueBorder: 'rgba(41,128,185,0.25)',
  serif: FONTS.serif,
  sans:  FONTS.sans,
  mono:  FONTS.mono,
}

export const T = new Proxy({}, {
  get(_, key) {
    return (_mode === 'light' ? LIGHT : DARK)[key]
  }
})

export const getPrice = (tier, size) => {
  const prices = {
    standard: { small: 19, medium: 39, big: 49 },
    luxury:   { small: 59, medium: 89, big: 99 },
  }
  return prices[tier]?.[size] || 19
}

export const S = {
  inp: {
    width: '100%',
    padding: '12px 16px',
    get background() { return T.bg3 },
    get border() { return `1px solid ${T.faint}` },
    borderRadius: '10px',
    get color() { return T.white },
    fontSize: '14px',
    get fontFamily() { return T.sans },
    outline: 'none',
    boxSizing: 'border-box',
  },
  btnGold: {
    width: '100%',
    padding: '14px',
    get background() { return T.gold },
    color: '#080808',
    border: 'none',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '1px',
    cursor: 'pointer',
    get fontFamily() { return T.sans },
    textTransform: 'uppercase',
  },
  btn: {
    width: '100%',
    padding: '14px',
    get background() { return T.gold },
    color: '#080808',
    border: 'none',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '1px',
    cursor: 'pointer',
    get fontFamily() { return T.sans },
    textTransform: 'uppercase',
  },
  btnGhost: {
    width: '100%',
    padding: '14px',
    background: 'transparent',
    get color() { return T.muted },
    get border() { return `1px solid ${T.faint}` },
    borderRadius: '12px',
    fontSize: '13px',
    cursor: 'pointer',
    get fontFamily() { return T.sans },
  },
  backBtn: {
    background: 'transparent',
    get border() { return `1px solid ${T.faint}` },
    get color() { return T.muted },
    padding: '8px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    get fontFamily() { return T.sans },
    flexShrink: 0,
  },
  pill: (active) => ({
    padding: '8px 16px',
    borderRadius: '20px',
    border: `1px solid ${active ? T.goldBorder : T.faint}`,
    background: active ? T.goldFaint : 'transparent',
    color: active ? T.gold : T.muted,
    fontSize: '12px',
    cursor: 'pointer',
    fontFamily: T.sans,
    transition: 'all .15s',
    whiteSpace: 'nowrap',
  }),
  badge: (tier) => ({
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '9px',
    fontWeight: '600',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: tier === 'luxury' ? T.gold : T.green,
    background: tier === 'luxury' ? T.goldFaint : T.greenFaint,
    border: `1px solid ${tier === 'luxury' ? T.goldBorder : T.greenBorder}`,
  }),
  sectionLabel: {
    fontSize: '9px',
    color: T.muted,
    letterSpacing: '3px',
    textTransform: 'uppercase',
    padding: '20px 24px 12px',
  },
}

export const formatError = (error, fallback = 'Something went wrong. Please try again.') => {
  if (!error) return fallback
  if (typeof error === 'string') return error
  if (error.message) return error.message
  if (error.details) return error.details
  if (error.hint) return error.hint
  return fallback
}

export const skeleton = {
  background: 'linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.1), rgba(255,255,255,0.04))',
  borderRadius: '12px',
}